import React, { useContext, useState, useEffect } from "react";
import ExamFooter from "../ExamFooter/ExamFooter";
import ExamSidebar from "../ExamSidebar/ExamSidebar";
import {
    ExamData, ExamDetails, MappingData, ResponsesData, SelectedQuestion,
} from "../context";
import ExamQuestionBody from "../ExamQuestionBody/ExamQuestionBody.index";
import { IOptionsData } from "../ExamQuestionBody/ExamQuestion/ExamQuestion.options";
import LiveExam from "../../../../Services/liveexam.services";
import toast from "react-hot-toast";
import { defaultTheme } from "../../../../components/nx";

const t = defaultTheme;

interface ISetUserAnswersData {
    markedForReview: boolean;
    questionId: string;
    optionsData: IOptionsData[] | undefined;
}

export default function ExamHome() {
    const examData: any = useContext(ExamData);
    const examDetails: any = useContext(ExamDetails);
    const { selectedQuestionId, setSelectedQuestionId } = useContext<any>(SelectedQuestion);
    const { responsesData } = useContext<any>(ResponsesData);

    const [isInstructorApproved, setIsInstructorApproved] = useState(false);
    const [userAnswersData, setUserAnswersData] = useState<ISetUserAnswersData>();

    const handleSectionChange = (sectionId: string) => {
        const sectionDetails = responsesData.questionArr.find((arr: any) => arr.section === sectionId);
        setSelectedQuestionId({ ...selectedQuestionId, section: sectionId, question: sectionDetails.questions[0] });
    };

    const onDisabled = async () => {
        try {
            const response = await LiveExam.getReviewStatus();
            if (response.status) setIsInstructorApproved(response.data.data.isDisabled);
        } catch (e) {
            setIsInstructorApproved(false);
        }
    };

    const getUserSavedAnswer = async () => {
        if (!selectedQuestionId) return;
        try {
            const res = await LiveExam.getUserSavedAnswerByQuestionId(selectedQuestionId.question);
            if (res.status === 200) {
                if (res.data.questionId) {
                    setUserAnswersData(res.data);
                } else {
                    setUserAnswersData({ markedForReview: false, questionId: selectedQuestionId.question, optionsData: [] });
                }
            }
        } catch (err) {
            setUserAnswersData({ markedForReview: false, questionId: selectedQuestionId.question, optionsData: [] });
        }
    };

    const handleAnswersDataChange = async (e: any) => {
        const update: any = { ...userAnswersData, questionId: e.questionId, optionsData: e.optionsData };
        if (update.optionsData.find((option: any) => option.flag === true) && update.markedForReview === false) {
            update.markedForReview = true;
            await handleSaveDataMarkForReview(update);
        }
        setUserAnswersData(update);
    };

    const handleMarkedForReviewChange = async (e: any) => {
        if (userAnswersData?.markedForReview !== e && userAnswersData?.questionId) {
            const update: any = { ...userAnswersData, markedForReview: e };
            setUserAnswersData(update);
            await handleSaveDataMarkForReview(update);
            await getUserSavedAnswer();
        }
    };

    const handleSaveDataMarkForReview = async (update: ISetUserAnswersData) => {
        if (update) {
            let tobe_saved_optionData: any = update.optionsData;
            if (update.optionsData) {
                tobe_saved_optionData = update.optionsData.filter((ans: any) => ans.value);
            }
            try {
                const res = await LiveExam.updatedResponseData({ ...update, optionsData: tobe_saved_optionData });
                if (res.status === 200) { toast.success("Response Saved"); return true; }
            } catch (e) {
                toast.error("Unable to Save Response"); return false;
            }
        }
        return false;
    };

    const handleSaveData = async () => {
        if (userAnswersData && userAnswersData.questionId) {
            let tobe_saved_optionData: any = userAnswersData.optionsData;
            if (userAnswersData.optionsData) {
                tobe_saved_optionData = userAnswersData.optionsData.filter((ans: any) => ans.value);
            }
            try {
                const res = await LiveExam.updatedResponseData({ ...userAnswersData, optionsData: tobe_saved_optionData });
                if (res.status === 200) { toast.success("Response Saved"); return true; }
            } catch (e) {
                toast.error("Unable to Save Response"); return false;
            }
        } else {
            toast.error("No Response available"); return false;
        }
    };

    const reloadUserAnswers = async () => { await getUserSavedAnswer(); };

    useEffect(() => {
        getUserSavedAnswer();
        onDisabled();
    }, [selectedQuestionId]);

    // Build sectionData from responsesData (same source the old ExamQuestionBody used)
    const currentSectionData = responsesData?.questionArr?.find(
        (res: any) => res.section === selectedQuestionId?.section
    );

    return (
        <div style={{ display: 'flex', flex: 1, minHeight: 0, fontFamily: '"Geist", ui-sans-serif, system-ui, -apple-system, sans-serif' }}>
            {/* Sidebar */}
            <ExamSidebar
                questionIndex={selectedQuestionId}
                sectionData={currentSectionData}
            />

            {/* Main content */}
            <main style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', background: t.paper }}>
                {/* Section tabs */}
                {examData?.data && examData.data.length > 1 && (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 0,
                        borderBottom: `1px solid ${t.border}`, background: t.surface,
                        padding: '0 40px',
                    }}>
                        {examData.data.map((data: any) => {
                            const isActive = selectedQuestionId?.section === data._id;
                            return (
                                <button
                                    key={data._id}
                                    onClick={() => handleSectionChange(data._id)}
                                    style={{
                                        padding: '12px 16px', border: 'none',
                                        background: 'transparent', cursor: 'pointer',
                                        fontSize: 13, fontWeight: isActive ? 500 : 400,
                                        color: isActive ? t.ink : t.inkSoft,
                                        borderBottom: isActive ? `2px solid ${t.accent}` : '2px solid transparent',
                                        fontFamily: 'inherit', textTransform: 'uppercase',
                                        letterSpacing: '0.04em',
                                    }}
                                >
                                    {data.sectionName}
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Question body */}
                <div style={{ flex: 1, padding: '28px 40px', width: '100%', boxSizing: 'border-box' }}>
                    <ExamQuestionBody
                        onUserResponseChange={handleAnswersDataChange}
                        userAnswers={userAnswersData?.optionsData || []}
                        disabled={isInstructorApproved}
                    />
                </div>

                {/* Footer */}
                <ExamFooter
                    reloadUserAnswers={reloadUserAnswers}
                    handleMarkedForReviewChange={handleMarkedForReviewChange}
                    onSaveClicked={handleSaveData}
                    defaultMarkforReviewState={userAnswersData?.markedForReview || false}
                />
            </main>
        </div>
    );
}
